import type { GetProps } from "tamagui";
import { useInteractiveElement } from "../interactive/useInteractiveElement";
import { Card, type CardProps } from "./Card";
type StyleOverrides = Pick<GetProps<typeof Card>, "borderColor" | "backgroundColor" | "hoverStyle" | "pressStyle">;
export type SelectionTone = "default" | "brand";
export type SelectionCardProps = CardProps & {
  selected?: boolean;
  /** `brand` accents selection and interaction in the brand lime. */
  tone?: SelectionTone;
};

// Static portion — applies whenever `selected`, even when non-interactive.
const selectedStaticStyles: StyleOverrides = {
  borderColor: "$borderIntense"
};

// Interactive portion — gated on `isInteractive` to avoid hover bleed on static
// cards. Each `hoverStyle` restates the resting `backgroundColor` to cancel the
// hover fill in `Card`'s `interactive` variant; drop it and the fill lifts.
const selectedInteractiveStyles: StyleOverrides = {
  hoverStyle: {
    backgroundColor: "$surface2",
    borderColor: "$borderIntenseHover"
  },
  pressStyle: {
    backgroundColor: "$surface2Hover",
    borderColor: "$borderIntenseHover"
  }
};
const brandStaticStyles: StyleOverrides = {
  borderColor: "$borderBrand",
  backgroundColor: "$bgBrand"
};
const brandInteractiveStyles: StyleOverrides = {
  hoverStyle: {
    backgroundColor: "$bgBrand",
    borderColor: "$borderBrandHover"
  },
  pressStyle: {
    backgroundColor: "$bgBrand",
    borderColor: "$borderBrandHover"
  }
};
const selectedDisabledStaticStyles: StyleOverrides = {
  borderColor: "$borderStrong"
};

// `$borderDefaultHover` is deliberately not used here — it resolves to the same
// neutral as `$borderMedium`, so hover would be a no-op against this border.
const unselectedStaticStyles: StyleOverrides = {
  borderColor: "$borderMedium"
};
const unselectedInteractiveStyles: StyleOverrides = {
  hoverStyle: {
    backgroundColor: "$surface2",
    borderColor: "$borderMediumHover"
  },
  pressStyle: {
    backgroundColor: "$surface2Hover",
    borderColor: "$borderMediumHover"
  }
};
const selectedDisabledInteractiveStyles: StyleOverrides = {
  hoverStyle: {
    backgroundColor: "$bgDisabledSubtle",
    borderColor: "$borderStrong"
  },
  pressStyle: {
    backgroundColor: "$bgDisabledSubtle",
    borderColor: "$borderStrong",
    scale: 1
  }
};

/**
 * Card with selection-state visual variants. Composes `Card` rather than
 * extending via `styled(Card)` — variant matching needs a styled config on
 * the base, and `Card` is a plain function component.
 */
export const SelectionCard = ({
  selected = false,
  tone = "default",
  haptic = true,
  ...rest
}: SelectionCardProps) => {
  const {
    isInteractive
  } = useInteractiveElement(rest);
  const {
    pressStyle: consumerPressStyle,
    hoverStyle: consumerHoverStyle,
    borderColor: consumerBorderColor,
    backgroundColor: consumerBackgroundColor,
    ...passthrough
  } = rest;

  // Variants merge on top of consumer values per sub-key. Conditional spread
  // on the JSX side: `prop={undefined}` overrides Tamagui's styled defaults
  // rather than falling through.
  const selectedActive = selected;
  const disabledSelectedActive = selected && rest.disabled === true;
  const brandActive = tone === "brand" && rest.disabled !== true;
  const brandHoverActive = brandActive && selected;
  const unselectedActive = !selected && rest.disabled !== true;
  const variantPressStyle = brandActive && isInteractive ? brandInteractiveStyles.pressStyle : selectedActive && isInteractive ? disabledSelectedActive ? selectedDisabledInteractiveStyles.pressStyle : selectedInteractiveStyles.pressStyle : unselectedActive && isInteractive ? unselectedInteractiveStyles.pressStyle : undefined;
  const variantHoverStyle = brandHoverActive && isInteractive ? brandInteractiveStyles.hoverStyle : selectedActive && isInteractive ? disabledSelectedActive ? selectedDisabledInteractiveStyles.hoverStyle : selectedInteractiveStyles.hoverStyle : unselectedActive && isInteractive ? unselectedInteractiveStyles.hoverStyle : undefined;
  const pressStyle = variantPressStyle != null || consumerPressStyle != null ? {
    ...consumerPressStyle,
    ...variantPressStyle
  } : undefined;
  const hoverStyle = variantHoverStyle != null || consumerHoverStyle != null ? {
    ...consumerHoverStyle,
    ...variantHoverStyle
  } : undefined;
  const borderColor = disabledSelectedActive ? selectedDisabledStaticStyles.borderColor : selectedActive ? brandActive ? brandStaticStyles.borderColor : selectedStaticStyles.borderColor : consumerBorderColor ?? (unselectedActive ? unselectedStaticStyles.borderColor : undefined);
  const backgroundColor = brandActive && selectedActive ? brandStaticStyles.backgroundColor : consumerBackgroundColor;
  return <Card {...passthrough} haptic={haptic} {...pressStyle !== undefined ? {
    pressStyle
  } : {}} {...hoverStyle !== undefined ? {
    hoverStyle
  } : {}} {...borderColor !== undefined ? {
    borderColor
  } : {}} {...backgroundColor !== undefined ? {
    backgroundColor
  } : {}} data-sentry-element="Card" data-sentry-component="SelectionCard" data-sentry-source-file="SelectionCard.tsx" />;
};